import i18next from "i18next";
import { getShortDate } from "utils.js";

// Constants
export const ALLOWED_HIERRACY_CATEGORIES = ['divisions', 'collections', "filter_tags", 'genders', 'age_groups'];

// Helper Functions
const translateMonthText = (monthStr) => {
  if (!monthStr || i18next.language !== "ja") return monthStr;
  const monthCode = monthStr.substring(0, 3).toUpperCase();
  const year = monthStr.substring(3);
  const translatedMonth = i18next.t(`months.${monthCode}`, { defaultValue: "" });
  return translatedMonth ? `${year}年${translatedMonth}` : monthStr;
};

export const isFilterVisibleFn = (filterName, filterValue, showSegmentFilterFlag) => {
  if (filterName === "segments" && !showSegmentFilterFlag) {
    return false;
  }
  return filterValue?.size > 0;
};

export const getFilterDisplayName = (filterName, segmentFilterLabel) => {
  const displayNames = {
    segments: segmentFilterLabel || i18next.t("tabsConfig.segments"),
    filter_tags: "Categories",
    genders: "Gender",
    category4: "Categories"
  };

  const defaultDisplay = displayNames[filterName] || filterName.replace(/_/g, " ");
  if (filterName === "segments") return defaultDisplay;
  return i18next.t(`filterDisplayNames.${filterName}`, { defaultValue: defaultDisplay });
};

const findExistingFilterOption = (visibleFilterOptions, filterOption) => 
  visibleFilterOptions.find(option => option?.get("name") === filterOption?.get("name"));

const shouldAddFilterOption = (visibleFilterOptions, filterOption) => {
  const isExistingFilterOption = findExistingFilterOption(visibleFilterOptions, filterOption);
  return isExistingFilterOption === undefined;
};

const filterBySegmentId = (filterOption, segmentNames) => 
  !segmentNames || segmentNames.size === 0 || segmentNames?.includes(filterOption?.get("segment_id"));

const filterByDivisionId = (filterOption, divisionNames) =>
  divisionNames?.size === 0 || divisionNames?.includes(filterOption?.get("division_id"));

const filterByCategory4Id = (filterOption, category4Names) =>
  category4Names?.size === 0 || category4Names?.includes(filterOption?.get("category4_id"));

const filterByCollectionId = (filterOption, collectionNames) =>
  collectionNames?.size === 0 || collectionNames?.includes(filterOption?.get("collection_id"));

export const filterValuesList = (
  category,
  filterOptions,
  filterSequence,
  selectedFilters,
  onToggle
) => {
  let visibleFilterOptions = [];

  const selectedSegmentsFilter = selectedFilters?.get("segments");
  const segmentNames = selectedSegmentsFilter?.map(segment => segment?.get("name"));

  if (filterSequence?.get(0) === category) {
    filterOptions?.forEach(filterOption => {
      if (shouldAddFilterOption(visibleFilterOptions, filterOption)) {
        visibleFilterOptions.push(filterOption);
      }
    });

    // Only for QuickFill category4 filters
    if (category === "category4") {
      const category3SelectedFilter = selectedFilters?.getIn(["category3", 0]);
      if (category3SelectedFilter) {
        visibleFilterOptions = visibleFilterOptions?.filter(filterOption => 
          filterOption?.get("parent_id") === category3SelectedFilter?.get("name")
        );
      }
    }
  } else {
    const selectedDivisionNamesFilter = selectedFilters?.get("divisions");
    const divisionNames = selectedDivisionNamesFilter?.map(division => division?.get("name"));

    const selectedCollectionNamesFilter = selectedFilters?.get("collections");
    const collectionNames = selectedCollectionNamesFilter?.map(collection => collection?.get("name"));

    const selectedCategory4NamesFilter = selectedFilters?.get("category4");
    const category4Names = selectedCategory4NamesFilter?.map(category4 => category4?.get("name"));

    filterOptions?.forEach(filterOption => {
      switch (category) {
        case "category4":
          if (filterBySegmentId(filterOption, segmentNames)) {
            if (shouldAddFilterOption(visibleFilterOptions, filterOption)) {
              visibleFilterOptions.push(filterOption);
            }
          }

          const category3SelectedFilter = selectedFilters?.getIn(["category3", 0]);
          if (category3SelectedFilter) {
            visibleFilterOptions = visibleFilterOptions?.filter(filterOption => 
              filterOption?.get("parent_id") === category3SelectedFilter?.get("name")
            );
          }
          break;

        case "divisions":
          if (filterBySegmentId(filterOption, segmentNames)) {
            if (shouldAddFilterOption(visibleFilterOptions, filterOption)) {
              visibleFilterOptions.push(filterOption);
            }
          }
          break;

        case "collections":
          if (filterByDivisionId(filterOption, divisionNames)) {
            if (shouldAddFilterOption(visibleFilterOptions, filterOption)) {
              if (segmentNames?.size > 0) {
                if (segmentNames.includes(filterOption?.get("segment_id"))) {
                  visibleFilterOptions.push(filterOption);
                }
              } else {
                visibleFilterOptions.push(filterOption);
              }
            }
          }
          break;

        default:
          if (filterOption?.get("category4_id")) {
            if (filterByCategory4Id(filterOption, category4Names)) {
              if (shouldAddFilterOption(visibleFilterOptions, filterOption)) {
                if (segmentNames?.size > 0) {
                  if (segmentNames.includes(filterOption?.get("segment_id"))) {
                    visibleFilterOptions.push(filterOption);
                  }
                } else {
                  visibleFilterOptions.push(filterOption);
                }
              }
            }
          } else if (filterByCollectionId(filterOption, collectionNames)) {
            if (shouldAddFilterOption(visibleFilterOptions, filterOption)) {
              if (segmentNames?.size > 0 && divisionNames?.size > 0) {
                if (segmentNames.includes(filterOption?.get("segment_id")) && 
                    divisionNames.includes(filterOption?.get("division_id"))) {
                  visibleFilterOptions.push(filterOption);
                }
              } else if (segmentNames?.size > 0) {
                if (segmentNames.includes(filterOption?.get("segment_id"))) {
                  visibleFilterOptions.push(filterOption);
                }
              } else if (divisionNames?.size > 0) {
                if (divisionNames.includes(filterOption?.get("division_id"))) {
                  visibleFilterOptions.push(filterOption);
                }
              } else {
                visibleFilterOptions.push(filterOption);
              }
            }
          }
      }
    });

  }

  visibleFilterOptions.sort((a, b) =>
    a?.get('name').localeCompare(b?.get('name'), undefined, { numeric: true })
  );

  return visibleFilterOptions.map((filterValueObj, index) => {
    const filterValue = filterValueObj?.get("name");
    const segmentId = filterValueObj?.get("segment_id");
    const divisionId = filterValueObj?.get("division_id");
    const collectionId = filterValueObj?.get("collection_id");

    const isChecked = selectedFilters?.get(category)?.find(obj => 
      obj?.get("name") === filterValue
    ) || false;

    const formattedValue = category === "delivery_months"
      ? translateMonthText(getShortDate(filterValue))
      : undefined;

    return {
      key: `${segmentId}-${divisionId}-${category}-${filterValue}-${index}`,
      filterName: filterValue,
      segment: segmentId,
      division: divisionId,
      collection: collectionId,
      category,
      filterValue,
      formattedValue,
      isChecked,
      onToggle
    };
  });
};
